import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Nav from "../components/Nav";
import { CheckCircle, Sparkle, Briefcase, Lightning, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";

const FEATURES_FREE = [
  "5 AI questions per day",
  "3 portfolio holdings",
  "Unlimited watchlist",
  "Live quotes & profit margin views",
];
const FEATURES_PRO = [
  "Unlimited AI questions (Claude Sonnet 4.5)",
  "Unlimited portfolio holdings",
  "Priority data refresh",
  "Weekly Portfolio Brief email",
  "Early access to new features",
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user?.is_pro) refresh?.(); }, []);

  const upgrade = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        plan_id: "pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <Nav />
      <main className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="label-eyebrow">Pricing</div>
          <h1 className="font-display text-5xl mt-1">Unlock the full lens.</h1>
          <p className="text-ink2 mt-3 max-w-2xl">Free is generous. Pro is unlimited — for the investor who wants to understand every position they own, without a cap.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-0 border border-black/10">
          {/* Free */}
          <div className="p-8 border-b md:border-b-0 md:border-r border-black/10">
            <div className="flex items-center gap-2">
              <div className="label-eyebrow">Free</div>
              {!user?.is_pro && <span className="text-[10px] px-2 py-0.5 bg-ink text-paper font-mono-num">CURRENT</span>}
            </div>
            <div className="mt-4">
              <span className="font-mono-num text-5xl">$0</span>
              <span className="text-ink2 ml-2">forever</span>
            </div>
            <p className="text-sm text-ink2 mt-4">Everything you need to start understanding stocks.</p>
            <ul className="mt-6 space-y-3">
              {FEATURES_FREE.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <CheckCircle weight="fill" size={16} className="text-ink2 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div className="p-8 bg-ikb text-paper relative overflow-hidden">
            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-coral opacity-30 blur-2xl pointer-events-none" />
            <div className="flex items-center gap-2 relative">
              <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.75)" }}>Pro</div>
              {user?.is_pro && <span className="text-[10px] px-2 py-0.5 bg-mint text-ink font-mono-num">ACTIVE</span>}
              <Crown weight="fill" size={14} className="text-amber2" />
            </div>
            <div className="mt-4">
              <span className="font-mono-num text-6xl font-medium">$20</span>
              <span className="opacity-80 ml-2">/ month</span>
            </div>
            <p className="text-sm opacity-90 mt-4">Unlimited access. Cancel anytime.</p>
            <ul className="mt-6 space-y-3">
              {FEATURES_PRO.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Sparkle weight="fill" size={16} className="text-amber2 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={upgrade}
              disabled={busy || user?.is_pro}
              className="mt-8 w-full px-4 py-3 bg-paper text-ink font-semibold hover:bg-amber2 hover:text-ink transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="pricing-upgrade-btn"
            >
              {user?.is_pro ? (<><CheckCircle weight="fill" size={16} /> You're on Pro</>) : busy ? "Redirecting to Stripe…" : (<><Lightning weight="fill" size={16} /> Upgrade to Pro — $20/mo</>)}
            </button>
            <p className="text-xs opacity-75 mt-3 text-center">Secure checkout via Stripe · Test mode</p>
          </div>
        </div>

        <div className="mt-12 border-t border-black/10 pt-8 grid md:grid-cols-3 gap-8 text-sm">
          <div>
            <Briefcase size={20} className="text-ikb" />
            <div className="font-display text-lg mt-2">Track everything</div>
            <p className="text-ink2 mt-1">Free tier caps at 3 holdings — enough for a starter portfolio. Pro removes the cap so you can track your full sheet.</p>
          </div>
          <div>
            <Sparkle size={20} className="text-coral" />
            <div className="font-display text-lg mt-2">Ask without limits</div>
            <p className="text-ink2 mt-1">Free tier gives 5 AI questions per day. Pro is unlimited — deep-dive any earnings report or margin trend.</p>
          </div>
          <div>
            <Crown size={20} className="text-amber2" weight="fill" />
            <div className="font-display text-lg mt-2">Support the roadmap</div>
            <p className="text-ink2 mt-1">Your subscription funds new integrations (news, alerts, brokerage sync) and keeps StockProfit Life ad-free.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
